import {
  Box,
  Tooltip,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  Icon,
  Text,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react';
import { BarsProgressDuotone } from './icons/bars-progress-duotone';
import { useFirebaseUser } from 'utils/useFirebaseUser';
import { useContext, useEffect, useMemo, useState } from 'react';
import { CustomDatePicker } from 'components/custom-datepicker';
import { DynamicTable } from 'components/dynamic-table';
import { FaLink, FaLinkedin } from 'react-icons/fa';
import { TweetContext } from 'context/tweetContext';
import { EyeIcon } from 'components/queue-panels/icons/eye-icon';
import { EditPageIcon } from 'components/queue-panels/icons/edit-page-icon';
import { SettingsPopup } from 'components/queue-panels/settings-popup';
import { useAddToQueue } from 'hooks/useAddToQueue';
import { getTopTweetsStats, getTweetsStats } from 'controllers/stats';
import { getAccount } from 'utils/sessionHelper';
import { useSession } from 'next-auth/react';
import { textStyle } from 'theme/names';
import numbro from 'numbro';
import moment from 'moment';
import { mapPostsPerformance, PostStat } from 'utils/mapPostPerformance';
import Link from 'next/link';

let maxImpression = 0;
let maxEngagement = 0;
let maxEngagementPercent = 0;
let maxLikes = 0;
let maxReplies = 0;
let maxShares = 0;

export const TopPostsTable = () => {
  const { data: session, status } = useSession() ?? {};
  const tweetContext: any = useContext(TweetContext);

  const [opendDraft, setOpendDraft] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dateSelectionObj, setDateSelectionObj] = useState<
    | {
        start: number;
        end: number;
      }
    | Date
  >(new Date(moment().subtract(30, 'days').startOf('day').toString()));
  const [topPosts, setTopPosts] = useState<PostStat[]>([]);
  const {
    isOpen: isOpenSettings,
    onOpen: onOpenSettings,
    onClose: onCloseSettings,
  } = useDisclosure();

  const handleAddToQueue = useAddToQueue();

  const handleDateChange = async (dateSelectionObj) => {
    setDateSelectionObj(dateSelectionObj.value);
  };

  const isAuthenticated = useFirebaseUser();
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const timeFrame = dateSelectionObj;
      let stats = await getTopTweetsStats(
        { ...getAccount(session) },
        timeFrame
      );
      const mappedPostPerformance = mapPostsPerformance(
        stats,
        getAccount(session)
      );

      maxImpression = mappedPostPerformance.maxImpression;
      maxEngagement = mappedPostPerformance.maxEngagement;
      maxEngagementPercent = mappedPostPerformance.maxEngagementPercent;
      maxLikes = mappedPostPerformance.maxLikes;
      maxReplies = mappedPostPerformance.maxReplies;
      maxShares = mappedPostPerformance.maxShares;
      setIsLoading(false);
      setTopPosts(mappedPostPerformance.stats);
    };

    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, dateSelectionObj]);

  const columns = useMemo(
    () => [
      {
        Header: 'Posts',
        accessor: 'previewText',
        disableSortBy: true,
        Cell: ({ value, row }) => {
          return (
            <Text
              textStyle={textStyle['body.medium.light']}
              minW='100%'
              maxW='320px'
              textAlign='start'
              noOfLines={1}
              wordBreak={'break-all'}
              isTruncated
            >
              {row?.original?.previewText?.text}
            </Text>
          );
        },
      },
      {
        Header: 'Likes',
        accessor: 'likes',
        isNumeric: true,
        Cell: ({ value }) => (
          <Text
            textStyle={textStyle['body.medium.light']}
            fontWeight={value && maxLikes == value ? '900' : '400'}
          >
            {numbro(value)?.format({ thousandSeparated: true })}
          </Text>
        ),
      },
      {
        Header: 'Replies',
        accessor: 'replies',
        isNumeric: true,
        Cell: ({ value }) => (
          <Text
            textStyle={textStyle['body.medium.light']}
            fontWeight={value && maxReplies == value ? '900' : '400'}
          >
            {numbro(value)?.format({ thousandSeparated: true })}
          </Text>
        ),
      },
      {
        Header: 'Shares',
        accessor: 'shares',
        isNumeric: true,
        Cell: ({ value }) => (
          <Text
            textStyle={textStyle['body.medium.light']}
            fontWeight={value && maxReplies == value ? '900' : '400'}
          >
            {numbro(value)?.format({ thousandSeparated: true })}
          </Text>
        ),
      },
      {
        Header: 'Views',
        accessor: 'impression',
        isNumeric: true,
        Cell: ({ value }) => (
          <Text
            textStyle={textStyle['body.medium.light']}
            fontWeight={value && maxImpression == value ? '900' : '400'}
          >
            {numbro(value)?.format({ thousandSeparated: true })}
          </Text>
        ),
      },
      {
        Header: 'Engagement',
        accessor: 'engagementPercent',
        Cell: ({ value }) => (
          <Text
            textStyle={textStyle['body.medium.light']}
            fontWeight={value && maxEngagementPercent == value ? '900' : '400'}
          >
            {value.toFixed(2) + '%'}
          </Text>
        ),
      },
      {
        accessor: 'actions',
        Header: (
          <Box display='flex' justifyContent='end'>
            Actions
          </Box>
        ),
        Cell: ({ value, row }) => (
          <Box display='flex' justifyContent='end'>
            <Tooltip hasArrow label='Open on linkedIn'>
              <IconButton
                mr={2}
                aria-label='Open on LinkedIn'
                variant='action'
                size='table'
                as='a'
                target='_blank'
                href={
                  'https://www.linkedin.com/feed/update/' +
                  row.original.ai.id_str
                }
                onClick={async (e) => {
                  e.stopPropagation();
                }}
                icon={<FaLinkedin color='#0A66C2' />}
              />
            </Tooltip>
            <Tooltip hasArrow label='Preview'>
              <IconButton
                aria-label='Preview'
                variant='action'
                size='table'
                mr={2}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpendDraft(row.original.ai);
                  onOpenSettings();
                }}
                icon={
                  <Icon fontSize='22px'>
                    <EyeIcon />
                  </Icon>
                }
              />
            </Tooltip>
            <Tooltip hasArrow label='Edit'>
              <IconButton
                aria-label='Edit'
                variant='action'
                size='table'
                onClick={(e) => {
                  e.stopPropagation();
                  tweetContext.newTweet(row.original.ai, row.original.ai.id);
                  tweetContext.setSelectedTweet(row.original.ai);
                  tweetContext.open();
                }}
                icon={
                  <Icon fontSize='22px'>
                    <EditPageIcon />
                  </Icon>
                }
              />
            </Tooltip>
          </Box>
        ),
      },
    ],
    []
  );

  return (
    <Card w='100%' flexShrink={0}>
      <CardHeader>
        <Heading size='sm'>
          <Flex gap={2} alignItems='center'>
            <BarsProgressDuotone />
            <Text>Detailed posts performance</Text>
            <Link href='/stats'>
              <FaLink size={12} />
            </Link>
          </Flex>
        </Heading>
        <Flex flexWrap={'wrap'} gap='4' justifyContent={'flex-end'}>
          <CustomDatePicker
            onChange={handleDateChange}
            returnDefaultSelection={true}
            showAlldPicker={true}
          />
        </Flex>
      </CardHeader>
      <CardBody>
        <DynamicTable
          data={topPosts || []}
          columns={columns}
          emptyMessage='No detailed posts performance insights'
        />
        <SettingsPopup
          isOpen={isOpenSettings}
          onClose={onCloseSettings}
          tweet={opendDraft || { text: '' }}
          hideAdvancedButton={false}
          showAddToQueue
          isGhostwriter={false}
          handleAddToQueue={handleAddToQueue(opendDraft)}
        />
      </CardBody>
    </Card>
  );
};
